/**
 * The mock backend's Polly part.
 *
 * Served over real HTTP by liquido-mock-http-server.js in the e2e mock mode - Polly operations on
 * /graphql and the passkey ceremony on /polly/webauthn/* - so that polly-client.js and
 * polly-passkey.js run exactly the code they run against a real backend, real WebAuthn ceremony
 * included (Chrome's virtual authenticator in Cypress). Like liquido-mock-domain.js it uses no Node
 * APIs, because vitest calls it in-process too: there is no dev server in a vitest run.
 *
 * Entirely separate from liquido-mock-domain.js: its own state, its own id space. A polly in here can
 * never turn up in the team's poll list, because the two mocks share no data at all.
 *
 * Identity: a polly session is the JWT the backend hands out after a passkey ceremony. Here it is the
 * string "mock-polly-jwt-<credentialId>", and every operation is told the caller's credential id -
 * taken from the request's Authorization header (HTTP), or from the session in localStorage (vitest).
 */

import config from "config"
import { PollyError, POLLY_STATUS } from "@/polly/services/polly-constants.js"

const deepClone = val => JSON.parse(JSON.stringify(val))
const DAY_MS = 24 * 60 * 60 * 1000
/** liquido.polly.email-confirmation-days: how long a confirm link works */
const EMAIL_CONFIRMATION_DAYS = 7
const nowIso = () => new Date().toISOString()

const createState = () => ({
	pollys: [],
	/** The credential ids of every passkey registered so far. */
	credentials: [],
	nextProposalId: 1,
	/** Only for the in-process passkey stand-in that vitest uses, see pollyPasskeyMock(). */
	nextCredentialId: 1,
	nextGuestId: 1,
	/** credential id -> the guest identity it took over (WebAuthnCredential.guestId) */
	guestOfCredential: {},
	/**
	 * credential id -> { email, token, expiresAt }: the email a passkey without an account last started a
	 * polly with, not confirmed yet, and the token of its confirm link (EmailConfirmationEntity)
	 */
	pending: {},
	/** { name, email, emailVerified }: the accounts that confirming created (UserEntity, teamless) */
	accounts: [],
	/** credential id -> the email of the account it belongs to (WebAuthnCredential.liquidoUser) */
	accountOfCredential: {},
	nextConfirmToken: 1,
})

/**
 * The polly "database". Module-scoped, so it lives as long as the process that imported it: the Vite
 * dev server (e2e), or a vitest run.
 */
let mockState = createState()

/** Back to an empty database: POST /mock/reset, and vitest between tests. */
export function resetPollyMockState() {
	mockState = createState()
}

const rejectPolly = (pollyErrorCode, msg) => {
	const err = new Error(msg)
	err.pollyErrorCode = pollyErrorCode
	throw err
}

/**
 * Short opaque public id.
 * Never the database key: with a sequential id the share link would be the only access
 * control, and /polly/1,2,3... would enumerate every polly ever created.
 */
const BASE58 = "123456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz"
const newPublicId = () => Array.from({ length: 10 },
	() => BASE58[Math.floor(Math.random() * BASE58.length)]).join("")

/* ===================== identity ===================== */

const POLLY_JWT_PREFIX = "mock-polly-jwt-"

/** The polly session for a passkey. The real backend mints a signed JWT after verifying the ceremony. */
export const pollyJwtFor = credentialId => POLLY_JWT_PREFIX + credentialId

/** The credential id a polly session stands for, or undefined for no (or a foreign) session. */
export const credentialOfPollyJwt = jwt =>
	typeof jwt === "string" && jwt.startsWith(POLLY_JWT_PREFIX) ? jwt.slice(POLLY_JWT_PREFIX.length) : undefined

/**
 * A passkey was registered: remember its credential id, and hand out a session for it. Registered from
 * a guest session, the passkey takes over the guest's identity (PollyWebAuthnRestApi.register).
 *
 * @param {String} credentialId the new passkey
 * @param {String} guestJwt the session the registration came with, if any
 */
export function registerPollyCredential(credentialId, guestJwt) {
	if (!mockState.credentials.includes(credentialId)) mockState.credentials.push(credentialId)
	const guestId = credentialOfPollyJwt(guestJwt)
	if (isGuestId(guestId)) mockState.guestOfCredential[credentialId] = guestId
	return pollyJwtFor(credentialId)
}

/** A guest's identity, "guest:<n>" - like the backend's "guest:<uuid>" subject. */
const isGuestId = id => typeof id === "string" && id.startsWith("guest:")

/** Who votes: a guest, the guest a passkey took over, or the passkey itself - PollySession.voterIdentity() */
const voterIdentity = id => mockState.guestOfCredential[id] || id

/** A passkey logged in: a session for a credential the mock knows, undefined for one it does not. */
export function loginPollyCredential(credentialId) {
	return mockState.credentials.includes(credentialId) ? pollyJwtFor(credentialId) : undefined
}

/** A passkey session - not a guest's: creating, owning and the profile need one. */
function requireCredential(credentialId) {
	if (!credentialId || isGuestId(credentialId)) rejectPolly(PollyError.NEED_PASSKEY, "No polly passkey session")
	return credentialId
}

/** Any polly session: a guest may vote. */
function requireVoter(credentialId) {
	if (!credentialId) rejectPolly(PollyError.NEED_PASSKEY, "No polly session")
	return voterIdentity(credentialId)
}

/**
 * Stands in for HMAC(serverSecret, credentialId + pollyId).
 * Per polly on purpose: the same person is unlinkable across different pollys.
 */
const voterKey = (credentialId, publicId) => `vk:${credentialId}:${publicId}`

/** Stands in for HMAC(serverSecret, credentialId) - stable, used for ownership and myPollys. */
const ownerKey = credentialId => `ok:${credentialId}`

/* ===================== data ===================== */

const findPolly = publicId => {
	const polly = mockState.pollys.find(p => p.publicId === publicId)
	return polly
}

const findPollyOrThrow = publicId => {
	const polly = findPolly(publicId)
	if (!polly) rejectPolly(PollyError.POLLY_NOT_FOUND, `Polly ${publicId} not found`)
	return polly
}

/** Names the way the backend compares them: trimmed, lower-cased. */
const nameKey = name => (name || "").trim().toLowerCase()

/** Shape a polly the way the backend would, for whoever is asking. */
function view(polly, credentialId) {
	return {
		publicId: polly.publicId,
		title: polly.title,
		status: polly.status,
		createdAt: polly.createdAt,
		votingEndAt: polly.votingEndAt,
		ownerName: polly.ownerName,
		numBallots: polly.ballots.length,
		// alphabetically, never in voting order - and never next to a ballot (PollyVoterEntity)
		voterNames: [...polly.voterNames].sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase())),
		isOwner: !!credentialId && !isGuestId(credentialId) && polly.ownerKey === ownerKey(credentialId),
		alreadyVoted: !!credentialId && polly.ballots.some(b => b.voterKey === voterKey(voterIdentity(credentialId), polly.publicId)),
		proposals: deepClone(polly.proposals),
		winner: polly.winner ? deepClone(polly.winner) : null,
	}
}

function requireOwner(polly, credentialId) {
	requireCredential(credentialId)
	if (polly.ownerKey !== ownerKey(credentialId)) {
		rejectPolly(PollyError.NOT_POLLY_OWNER, "Only the polly owner may do that")
	}
}

/**
 * Tideman Ranked Pairs, simplified here to counting first preferences.
 * The real backend shares the full implementation with LIQUIDO polls - that is the one
 * piece of logic the two products genuinely have in common.
 */
function determineWinner(polly) {
	if (!polly.ballots.length) return null   // nobody voted: no winner
	const firstPreferences = new Map()
	polly.ballots.forEach(ballot => {
		const top = ballot.voteOrder[0]
		if (top != null) firstPreferences.set(String(top), (firstPreferences.get(String(top)) || 0) + 1)
	})
	return polly.proposals
		.slice()
		.sort((a, b) => (firstPreferences.get(String(b.id)) || 0) - (firstPreferences.get(String(a.id)) || 0))[0] || null
}

const handlers = {

	createPolly: (variables, credentialId) => {
		requireCredential(credentialId)
		const titles = (variables.proposalTitles || []).map(t => t?.trim()).filter(Boolean)
		if (!variables.title?.trim()) rejectPolly(PollyError.INVALID_POLLY, "A polly needs a title")
		if (titles.length < 2) rejectPolly(PollyError.INVALID_POLLY, "A polly needs at least two options")
		// The name for THIS polly, as typed - a creator chooses it per polly; no name is kept on the passkey
		const ownerName = requireName(variables.ownerName)
		// Without an account the address is needed, and waits unconfirmed; with one, the account's is used
		if (!accountOf(credentialId)) rememberPending(credentialId, requireEmail(variables.ownerEmail))

		const polly = {
			publicId: newPublicId(),
			title: variables.title.trim(),
			// Live immediately. There is no elaboration phase and no start step.
			status: POLLY_STATUS.VOTING,
			createdAt: nowIso(),
			votingEndAt: null,
			ownerKey: ownerKey(credentialId),
			ownerName,
			linkMailSent: false,
			proposals: titles.map(title => ({ id: String(mockState.nextProposalId++), title })),
			ballots: [],
			// Kept apart from the ballots, like the backend's polly_voter table
			voterNames: [],
			winner: null,
		}
		mockState.pollys.push(polly)
		return view(polly, credentialId)
	},

	polly: (variables, credentialId) => view(findPollyOrThrow(variables.publicId), credentialId),

	editPolly: (variables, credentialId) => {
		const polly = findPollyOrThrow(variables.publicId)
		requireOwner(polly, credentialId)
		if (polly.status === POLLY_STATUS.FINISHED) rejectPolly(PollyError.POLLY_FINISHED, "Polly is finished")
		if (polly.ballots.length > 0) {
			rejectPolly(PollyError.POLLY_ALREADY_STARTED, "Cannot change a polly once somebody has voted")
		}
		const titles = (variables.proposalTitles || []).map(t => t?.trim()).filter(Boolean)
		if (titles.length < 2) rejectPolly(PollyError.INVALID_POLLY, "A polly needs at least two options")

		polly.title = variables.title.trim()
		polly.proposals = titles.map((title, i) => ({
			id: polly.proposals[i]?.id || String(mockState.nextProposalId++),
			title,
		}))
		return view(polly, credentialId)
	},

	voteInPolly: (variables, credentialId) => {
		const voter = requireVoter(credentialId)   // a guest may vote
		const polly = findPollyOrThrow(variables.publicId)
		if (polly.status === POLLY_STATUS.FINISHED) rejectPolly(PollyError.POLLY_FINISHED, "Polly is finished")

		const key = voterKey(voter, polly.publicId)
		// Stands in for UNIQUE(polly_id, voter_key). One vote per passkey, enforced by the
		// database rather than by application logic that could be forgotten in some code path.
		if (polly.ballots.some(b => b.voterKey === key)) {
			rejectPolly(PollyError.ALREADY_VOTED, "You already voted in this polly")
		}
		// The creator votes under the polly's ownerName, and nobody else may use it. A friend - guest or
		// passkey - chooses a name for each polly (PollyService.voteInPolly).
		const isOwner = !!voter && polly.ownerKey === ownerKey(credentialId)
		const voterName = isOwner && polly.ownerName ? polly.ownerName : requireName(variables.voterName)
		if (!isOwner && polly.ownerName && nameKey(voterName) === nameKey(polly.ownerName)) {
			rejectPolly(PollyError.VOTER_NAME_TAKEN, "This is the name of the polly's creator")
		}
		// ... and UNIQUE(polly_id, name_key): one name votes once
		if (polly.voterNames.some(name => nameKey(name) === nameKey(voterName))) {
			rejectPolly(PollyError.VOTER_NAME_TAKEN, "Somebody already voted under this name")
		}

		const validIds = new Set(polly.proposals.map(p => String(p.id)))
		const voteOrder = (variables.voteOrder || []).map(String)
		if (voteOrder.length === 0 || voteOrder.some(id => !validIds.has(id))) {
			rejectPolly(PollyError.INVALID_POLLY, "Invalid vote order")
		}

		polly.ballots.push({ voterKey: key, voteOrder })
		polly.voterNames.push(voterName)
		return view(polly, credentialId)
	},

	finishPolly: (variables, credentialId) => {
		const polly = findPollyOrThrow(variables.publicId)
		requireOwner(polly, credentialId)
		if (polly.status === POLLY_STATUS.FINISHED) return view(polly, credentialId)   // idempotent
		polly.status = POLLY_STATUS.FINISHED
		polly.votingEndAt = nowIso()			// the moment it was finished
		polly.winner = determineWinner(polly)
		return view(polly, credentialId)
	},

	/** Newest first, like PollyEntity.findByOwnerKey. The mock appends, so reversing is that order. */
	/** PollyService.newGuestSession: a friend votes without a passkey */
	pollyGuestSession: () => pollyJwtFor("guest:" + mockState.nextGuestId++),

	/** PollyService.myProfile: the account's name and email, else the passkey's name and the email last given */
	pollyProfile: (variables, credentialId) => profileOf(requireCredential(credentialId)),

	/**
	 * PasskeyAccountService.confirm: the link in the link mail, opened with the passkey the polly was
	 * started with. Creates the account, or joins a confirmed one. The same order of checks.
	 */
	confirmPollyEmail: (variables, credentialId) => {
		requireCredential(credentialId)
		const token = variables.token
		const [owner, pending] = Object.entries(mockState.pending).find(([, p]) => token && p.token === token) || []
		if (!pending) {
			// Confirming deleted it: a second click on the same link says "already confirmed"
			if (accountOf(credentialId)?.emailVerified) return { alreadyConfirmed: true, profile: profileOf(credentialId) }
			rejectPolly(PollyError.EMAIL_CONFIRMATION_INVALID, "This link is not valid (any more)")
		}
		if (Date.now() >= pending.expiresAt) rejectPolly(PollyError.EMAIL_CONFIRMATION_EXPIRED, "This link has expired")
		if (owner !== credentialId) rejectPolly(PollyError.WRONG_PASSKEY, "This link must be opened with the passkey the polly was started with")
		let account = mockState.accounts.find(a => a.email === pending.email)
		if (account && !account.emailVerified) {
			rejectPolly(PollyError.ACCOUNT_EMAIL_UNVERIFIED, "An account with this address exists, but its address is not confirmed yet")
		}
		if (!account) {
			account = { name: latestOwnerName(credentialId), email: pending.email, emailVerified: true }
			mockState.accounts.push(account)
		}
		mockState.accountOfCredential[credentialId] = account.email
		delete mockState.pending[credentialId]
		return { alreadyConfirmed: false, profile: profileOf(credentialId) }
	},

	myPollys: (variables, credentialId) => {
		const key = ownerKey(requireCredential(credentialId))
		return mockState.pollys.filter(p => p.ownerKey === key).reverse().map(polly => view(polly, credentialId))
	},
}

/** A (nick)name of the creator or a voter, like PollyService.requireName: trimmed, at least usernameMinLength, not too long. */
function requireName(name) {
	const clean = (name || "").trim()
	if (!clean) rejectPolly(PollyError.INVALID_POLLY, "Please tell us your name")
	if (clean.length < (Number(config.usernameMinLength) || 3)) rejectPolly(PollyError.INVALID_POLLY, "That name is too short")
	if (clean.length > (Number(config.pollyNameMaxLength) || 40)) rejectPolly(PollyError.INVALID_POLLY, "That name is too long")
	return clean
}

function requireEmail(email) {
	const clean = (email || "").trim().toLowerCase()
	if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(clean)) rejectPolly(PollyError.INVALID_POLLY, "A polly needs a valid email address")
	return clean
}

/* ===================== accounts: a passkey, its pending address, its account ===================== */

/** The account a passkey belongs to, or undefined */
function accountOf(credentialId) {
	const email = mockState.accountOfCredential[credentialId]
	return email ? mockState.accounts.find(a => a.email === email) : undefined
}

/** PollyService.rememberPendingEmail: a different address starts over - an old link must not confirm it */
function rememberPending(credentialId, email) {
	const pending = mockState.pending[credentialId]
	if (!pending || pending.email !== email) mockState.pending[credentialId] = { email, token: null, expiresAt: null }
}

/** PollyEntity.latestOwnerName: the name of the polly this passkey created most recently, else undefined */
function latestOwnerName(credentialId) {
	const key = ownerKey(credentialId)
	return [...mockState.pollys].reverse().find(p => p.ownerKey === key && p.ownerName)?.ownerName
}

/** PollyService.nameOf: the account's name, else the name of the newest polly this passkey created, else undefined */
function nameOf(credentialId) {
	return accountOf(credentialId)?.name ?? latestOwnerName(credentialId)
}

function profileOf(credentialId) {
	const account = accountOf(credentialId)
	if (account) return { name: account.name, email: account.email, hasAccount: true, emailConfirmed: account.emailVerified }
	const pending = mockState.pending[credentialId]
	return { name: nameOf(credentialId) ?? null, email: pending?.email ?? null, hasAccount: false, emailConfirmed: false }
}

/** A new confirm token for a passkey without an account - only the newest mail's link confirms. Undefined with an account. */
function confirmTokenFor(credentialId) {
	const pending = mockState.pending[credentialId]
	if (accountOf(credentialId) || !pending) return undefined
	pending.token = "mock-confirm-" + mockState.nextConfirmToken++
	pending.expiresAt = Date.now() + EMAIL_CONFIRMATION_DAYS * DAY_MS
	return pending.token
}

/**
 * POST /polly/:publicId/link-mail - PollyMailRestApi. Owner only, once per polly. The recipient is the
 * passkey's account, else the address given on the start page.
 * @returns {{ to, path, confirmToken }} what the "mail" would carry - confirmToken only while the address is
 *   not confirmed - or null when it was mailed already, or there is nobody to mail. Throws like an operation.
 */
export function claimPollyLinkMail(publicId, credentialId) {
	const polly = findPollyOrThrow(publicId)
	requireOwner(polly, credentialId)
	if (polly.linkMailSent) return null
	const to = accountOf(credentialId)?.email ?? mockState.pending[credentialId]?.email
	if (!to) return null
	polly.linkMailSent = true
	return { to, path: "/polly/" + polly.publicId, confirmToken: confirmTokenFor(credentialId) }
}

/** The Polly operation a GraphQL query string asks for, or undefined for any other (team) query. Longest names first. */
export function detectPollyOperation(query) {
	for (const name of ["confirmPollyEmail", "pollyGuestSession", "pollyProfile", "createPolly", "editPolly", "voteInPolly", "finishPolly", "myPollys", "polly"]) {
		if (new RegExp(`\\b${name}\\b`).test(query || "")) return name
	}
	return undefined
}

/**
 * Run one Polly operation for the caller with this credential id (undefined: no session) and return
 * its GraphQL `data` payload. Throws an Error carrying `pollyErrorCode` where the polly backend would
 * answer with an error.
 */
export function runPollyOperation(query, variables = {}, credentialId) {
	const operation = detectPollyOperation(query)
	if (!operation) throw new Error("POLLY MOCK: unhandled operation in " + query)
	return { [operation]: handlers[operation](variables || {}, credentialId) }
}

/* ============ in-process stand-ins for vitest, which has no dev server and no authenticator ============ */

/**
 * polly-client.js's pollyQuery() under vitest: resolves { data }, rejects { pollyErrorCode, msg } -
 * the same shapes as the HTTP path.
 */
export function pollyQueryMock(query, variables = {}, sessionJwt) {
	try {
		return Promise.resolve({ data: runPollyOperation(query, variables, credentialOfPollyJwt(sessionJwt)) })
	} catch (err) {
		return Promise.reject({ pollyErrorCode: err.pollyErrorCode, msg: err.message })
	}
}

/**
 * polly-passkey.js's two ceremonies under vitest. `register` mints a new credential, i.e. a new person -
 * taking over the guest of `guestJwt`, if that is a guest session;
 * `login` returns the newest one and fails without any - which is exactly what makes
 * ensurePasskeySession() fall through to registration on a first visit.
 */
export async function pollyPasskeyMock(kind, guestJwt) {
	if (kind === "register") {
		return { jwt: registerPollyCredential("mock-credential-" + mockState.nextCredentialId++, guestJwt) }
	}
	const credentialId = mockState.credentials[mockState.credentials.length - 1]
	if (!credentialId) {
		return Promise.reject({ pollyErrorCode: PollyError.NEED_PASSKEY, msg: "No passkey on this device" })
	}
	return { jwt: pollyJwtFor(credentialId) }
}
